import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Box, Chip, Tooltip, Typography } from '@mui/material'
import LockOutlinedIcon from '@mui/icons-material/LockOutlined'
import CancelIcon from '@mui/icons-material/Cancel'
import { black, grey } from '../../../../../colors'
import { type IAttribute } from '../../../../../models/Attributes'
import CustomTooltip from '../../../../../components/CustomTooltip'
import { useFormContext } from 'react-hook-form'
import MultiSelectApiAutoComplete from '../../../../../components/FormElements/MultiSelectApiAutoComplete'
import { useCurrentUserAttributeScope } from '../../../hooks/useCurrentUserAttributeScope'
import { scopedSliceKey as scopeKey } from '../../helper'

interface SlicesSectionProps {
  name: string
}

const SlicesSection: React.FC<SlicesSectionProps> = ({ name }) => {
  const { watch, setValue } = useFormContext()
  const maxSlices = 5
  const selectedSlices: IAttribute[] = watch(name) ?? []
  const canAddMore = selectedSlices.length < maxSlices

  const { appliedAttributeScope } = useCurrentUserAttributeScope()
  const scopedKeys = useMemo(
    () => new Set(appliedAttributeScope.courseAttributes.map(e => scopeKey(e.title, e.value))),
    [appliedAttributeScope]
  )
  const isScoped = useCallback(
    (option: IAttribute): boolean =>
      scopedKeys.has(scopeKey(option.attributeKey ?? '', option.attributeValue ?? '')),
    [scopedKeys]
  )

  // The MultiSelect's "Select All" toggle can wipe scoped chips even though
  // they're disabled in the dropdown — and the user has no way to re-add them
  // afterwards. Cache the loaded options here so the guard effect below can
  // restore them when they drop out of the selection.
  const [loadedOptions, setLoadedOptions] = useState<IAttribute[]>([])

  const handleOptionsLoaded = useCallback(
    (options: IAttribute[]): void => {
      if (options.length === 0) return
      setLoadedOptions(options)
      const preSelected = options.filter(o => o.isSelected)
      if (preSelected.length > 0) {
        setValue(name, preSelected)
      }
    },
    [name, setValue]
  )

  // Pre-select scoped catalogue entries when scope or options change, and
  // restore them if they get removed (e.g., via Select All). Scoped entries
  // not present in the catalogue (e.g., a role still pinned to a deleted
  // value) are silently dropped.
  useEffect(() => {
    if (scopedKeys.size === 0 || loadedOptions.length === 0) return
    const currentIds = new Set(selectedSlices.map(o => o.attributeValueId))
    const missing = loadedOptions.filter(o => isScoped(o) && !currentIds.has(o.attributeValueId))
    if (missing.length > 0) {
      setValue(name, [...missing, ...selectedSlices])
    }
  }, [scopedKeys, loadedOptions, selectedSlices, isScoped, setValue, name])

  const renderTags = (selected: IAttribute[]): React.ReactNode => (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'flex-start', maxWidth: '100%', whiteSpace: 'normal' }}>
      {selected.map((option, index) => {
        const locked = isScoped(option)
        const key = option.attributeValueId ?? `${option.attributeKey}-${index}`
        if (locked) {
          return (
            <Tooltip key={key} title="Locked by your role scope" arrow>
              <Chip
                color="primary"
                icon={<LockOutlinedIcon sx={{ fontSize: 16 }} />}
                label={option.attributeValue}
              />
            </Tooltip>
          )
        }
        return (
          <Chip
            key={key}
            color="primary"
            label={option.attributeValue}
            deleteIcon={<CancelIcon sx={{ color: `${grey[400]} !important` }} />}
            onDelete={() => {
              setValue(name, selected.filter((_, i) => i !== index))
            }}
          />
        )
      })}
    </Box>
  )

  return (
    <>
      <Typography color={black[100]} fontWeight={700}>Slice Data</Typography>
      <CustomTooltip title={!canAddMore ? `You can only add up to ${maxSlices} slices` : ''}>
        <MultiSelectApiAutoComplete
          apiEndpoint='/api/attributes/values/'
          apiExtraParams={{ attributeType: 'Course Attributes', pageSize: 20, isReportEnabled: true }}
          label='Course Attributes'
          placeholder="Select Course Attributes"
          name={name}
          getOptionLabel={(option: IAttribute) => option.attributeValue ?? ''}
          getOptionValue={(option: IAttribute) => option.attributeValueId ?? ''}
          getOptionSubLabel={(option: IAttribute) => option.attributeKey}
          renderTagsType='chips'
          renderTags={renderTags}
          getOptionDisabled={(option: IAttribute) =>
            isScoped(option) ||
            (!canAddMore && !selectedSlices.some(s => s.attributeValueId === option.attributeValueId))
          }
          onOptionsLoaded={handleOptionsLoaded}
        />
      </CustomTooltip>
    </>
  )
}

export default SlicesSection
